// Pricing Manager Class
// Handles business logic and state management for the pricing section

import { Manager } from '../../../core';
import { logger } from '../../../core';

export class PricingManager extends Manager {
  constructor() {
    super();
    this.plans = [];
    this.loading = true;
    this.error = null;
    this.isInitialized = false;
    this.currentUser = null;
    this.userMembership = null;
    this.localCardConfig = null;
    this.creditPacks = [];
  }

  /**
   * Initialize global variables and setup
   */
  async initGlobalVariables() {
    logger.log({
      tags: "manager|pricing|init",
      color1: "cyan"
    }, "Initializing PricingManager");
  }

  /**
   * Initialize states and data
   */
  async initStates() {
    logger.log({
      tags: "manager|pricing|ready",
      color1: "lime"
    }, "PricingManager states initialized");

    // Load plans from API
    await this.loadPlans();

    // Load current user membership (if authenticated)
    await this.loadUserMembership();

    // Load local card config (pricing page Local card)
    await this.loadLocalCardConfig();

    // Load credit packs (public endpoint, active only)
    await this.loadCreditPacks();

    this.isInitialized = true;
  }

  /**
   * Load active credit packs for the pricing page
   */
  async loadCreditPacks() {
    try {
      const response = await fetch('/api/credit-packs', { credentials: 'include' });
      const data = await response.json();
      if (response.ok && data.success) {
        this.creditPacks = Array.isArray(data.packs) ? data.packs : [];
      } else {
        this.creditPacks = [];
      }
    } catch (err) {
      this.creditPacks = [];
      logger.log({
        tags: "manager|pricing|credit-packs",
        color: "yellow"
      }, "Failed to load credit packs");
    }
  }

  getCreditPacks() {
    return this.creditPacks;
  }

  /**
   * Start Stripe checkout for a credit pack (one-time purchase)
   */
  async startPackCheckout(packId) {
    const response = await fetch('/api/stripe/create-pack-checkout', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ packId }),
    });
    const data = await response.json();
    if (!response.ok || !data.success) {
      throw new Error(data?.error || 'Failed to start pack checkout');
    }
    if (data.url) {
      window.location.href = data.url;
    }
    return data;
  }

  /**
   * Load current user's membership info
   */
  async loadUserMembership() {
    try {
      const response = await fetch('/api/account/me', {
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.user) {
          this.currentUser = data.user;
          // Handle membership object format
          const membership = data.user.membership;
          if (membership && typeof membership === 'object' && membership.planId) {
            this.userMembership = membership;
          } else {
            this.userMembership = null;
          }

          logger.log({
            tags: "manager|pricing|user",
            color: "cyan"
          }, `Loaded user membership: ${this.userMembership?.planId || 'none'}`);
        }
      } else if (response.status === 401) {
        // User not authenticated - that's okay, pricing is public
        logger.log({
          tags: "manager|pricing|user",
          color: "yellow"
        }, "User not authenticated (pricing is public)");
        this.currentUser = null;
        this.userMembership = null;
      }
    } catch (err) {
      // User might not be authenticated - that's okay, pricing is public
      logger.log({
        tags: "manager|pricing|user",
        color: "yellow"
      }, "User not authenticated or failed to load membership (pricing is public)");
      this.currentUser = null;
      this.userMembership = null;
    }
  }

  /**
   * Check if a plan is the user's current plan
   */
  isCurrentPlan(planId) {
    if (!this.userMembership) return false;
    
    // Check if planId matches user's membership planId
    const userPlanId = this.userMembership.planId;
    if (typeof userPlanId === 'string') {
      return userPlanId.toLowerCase() === planId.toLowerCase();
    }
    
    return false;
  }

  /**
   * Get current user membership
   */
  getUserMembership() {
    return this.userMembership;
  }

  /**
   * Get current user
   */
  getCurrentUser() {
    return this.currentUser;
  }

  /**
   * Open Stripe customer portal for subscription management
   */
  async openCustomerPortal() {
    try {
      logger.log({
        tags: "manager|pricing|action",
        color: "cyan"
      }, "Opening Stripe customer portal");

      const response = await fetch('/api/stripe/customer-portal', {
        credentials: 'include',
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data?.error || 'Failed to open customer portal');
      }

      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error('Customer portal URL not returned');
      }
    } catch (error) {
      logger.error({
        tags: "manager|pricing|error",
        color1: "red"
      }, "Customer portal error:", error);
      throw error;
    }
  }

  /**
   * Reactivate a subscription that's scheduled to cancel
   */
  async reactivateSubscription(subscriptionId) {
    try {
      logger.log({
        tags: "manager|pricing|action",
        color: "cyan"
      }, `Reactivating subscription: ${subscriptionId}`);

      const response = await fetch('/api/stripe/reactivate-subscription', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          subscriptionId,
        }),
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data?.error || 'Failed to reactivate subscription');
      }

      logger.log({
        tags: "manager|pricing|action",
        color: "lime"
      }, `Subscription reactivated successfully: ${subscriptionId}`);

      // Reload user membership to reflect the change
      await this.loadUserMembership();

      return data;
    } catch (error) {
      logger.error({
        tags: "manager|pricing|error",
        color1: "red"
      }, "Reactivate subscription error:", error);
      throw error;
    }
  }

  /**
   * Cancel any active subscription and downgrade to Free.
   * Keeps current credits (no decrease) based on backend membership rules.
   */
  async cancelToFree() {
    try {
      logger.log({
        tags: "manager|pricing|action",
        color: "cyan"
      }, "Canceling subscription and switching to Free");

      const response = await fetch('/api/stripe/cancel-to-free', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({}),
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data?.error || 'Failed to cancel and downgrade');
      }

      // Reload membership after downgrade
      await this.loadUserMembership();
      return data;
    } catch (error) {
      logger.error({
        tags: "manager|pricing|error",
        color1: "red"
      }, "Cancel to free error:", error);
      throw error;
    }
  }

  /**
   * Load plans from API
   */
  async loadPlans() {
    try {
      this.loading = true;
      this.error = null;

      const response = await fetch('/api/plans', {
        credentials: 'include',
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data?.error || 'Failed to fetch plans');
      }

      // Filter for main plans: free, pro, max
      const mainPlans = (data.plans || []).filter(plan =>
        ['free', 'pro', 'max'].includes(plan.uniqueId?.toLowerCase())
      );

      this.plans = mainPlans;
    } catch (err) {
      this.error = err.message || 'Failed to load plans';
      this.plans = [];
      logger.error({
        tags: "manager|pricing|error",
        color1: "red"
      }, "Failed to load plans:", err);
    } finally {
      this.loading = false;
    }
  }

  /**
   * Load local card config from API (display name, description, price, linked product)
   */
  async loadLocalCardConfig() {
    try {
      const response = await fetch('/api/products/local-card-config', {
        credentials: 'include',
      });
      const data = await response.json();
      if (response.ok && data.success) {
        this.localCardConfig = data.config || null;
      } else {
        this.localCardConfig = null;
      }
    } catch (err) {
      this.localCardConfig = null;
      logger.log({
        tags: "manager|pricing|local-card",
        color: "yellow"
      }, "Failed to load local card config, using defaults");
    }
  }

  /**
   * Get local card config
   */
  getLocalCardConfig() {
    return this.localCardConfig;
  }

  /**
   * Save local card config (admin only)
   */
  async saveLocalCardConfig(config) {
    const response = await fetch('/api/admin/products/local-card-config', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(config),
    });
    const data = await response.json();
    if (!response.ok || !data.success) {
      throw new Error(data?.error || 'Failed to save local card config');
    }
    this.localCardConfig = data.config || null;
    return data.config;
  }

  /**
   * Get plans
   */
  getPlans() {
    return this.plans;
  }

  /**
   * Get loading state
   */
  getLoading() {
    return this.loading;
  }

  /**
   * Get error state
   */
  getError() {
    return this.error;
  }

  /**
   * Handle plan selection - creates Stripe checkout session and redirects
   * Returns an object with action type and data for the UI to handle
   */
  async handlePlanSelect(planData, options = {}) {
    try {
      const { planId, tierId, billingCycle } = planData;
      const { reactivateSubscription: shouldReactivate, confirmPlanSwitch } = options;

      if (!planId || !tierId || !billingCycle) {
        throw new Error('Missing required plan data: planId, tierId, and billingCycle are required');
      }

      // If this is a confirmed plan switch or reactivation, proceed directly to checkout
      if (confirmPlanSwitch || shouldReactivate) {
        logger.log({
          tags: "manager|pricing|action",
          color: "cyan"
        }, `Plan switch/reactivation confirmed: ${planId} / ${tierId} (${billingCycle}) - Creating Stripe checkout session`);
        
        // Proceed directly to checkout creation
        return await this.createCheckoutSession(planData, { reactivateSubscription: shouldReactivate, confirmPlanSwitch: confirmPlanSwitch });
      }

      // First, check for subscription conflicts WITHOUT creating checkout
      logger.log({
        tags: "manager|pricing|action",
        color: "cyan"
      }, `Plan selected: ${planId} / ${tierId} (${billingCycle}) - Checking for existing subscriptions`);
      
      logger.log({
        tags: "manager|pricing|debug",
        color: "yellow"
      }, `Checking subscription status:`, { planId, tierId, billingCycle });

      const checkResponse = await fetch('/api/stripe/check-subscription', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          planId,
          tierId,
          billingCycle,
        }),
      });

      const checkData = await checkResponse.json();

      if (!checkResponse.ok || !checkData.success) {
        throw new Error(checkData.message || 'Failed to check subscription status');
      }

      // Handle different actions from the check
      if (checkData.action === 'CAN_REACTIVATE') {
        logger.log({
          tags: "manager|pricing|action",
          color: "cyan"
        }, "Subscription can be reactivated - showing reactivation modal");
        return {
          action: 'reactivate',
          subscriptionId: checkData.subscriptionId,
          currentPlan: checkData.currentPlan,
          message: checkData.message,
        };
      }

      if (checkData.action === 'PLAN_SWITCH_REQUIRED') {
        logger.log({
          tags: "manager|pricing|action",
          color: "cyan"
        }, "Plan switch required - showing plan switch modal");
        return {
          action: 'planSwitch',
          currentPlan: checkData.currentPlan,
          newPlan: checkData.newPlan,
          message: checkData.message,
          planData: planData, // Store original planData for retry
        };
      }

      if (checkData.action === 'ALREADY_SUBSCRIBED') {
        throw new Error(checkData.message || 'You already have an active subscription for this plan');
      }

      if (checkData.action === 'CANCEL_TO_FREE_REQUIRED') {
        return {
          action: 'cancelToFree',
          currentPlan: checkData.currentPlan,
          newPlan: checkData.newPlan,
          message: checkData.message,
          planData: planData,
        };
      }

      if (checkData.action === 'ALREADY_FREE') {
        // Nothing to do
        return { action: 'alreadyFree', message: checkData.message };
      }

      // No conflicts - proceed with checkout
      if (checkData.action === 'PROCEED') {
        logger.log({
          tags: "manager|pricing|action",
          color: "cyan"
        }, "No subscription conflicts - proceeding with checkout");
        return await this.createCheckoutSession(planData, {});
      }

      // Fallback - should not reach here
      throw new Error('Unexpected response from subscription check');
    } catch (error) {
      logger.error({
        tags: "manager|pricing|error",
        color1: "red"
      }, "Checkout error:", error);
      throw error;
    }
  }

  /**
   * Creates a Stripe checkout session
   * @param {Object} planData - Plan selection data
   * @param {Object} options - Options for checkout (reactivateSubscription, confirmPlanSwitch)
   * @returns {Promise<Object>} Checkout session URL or action object
   */
  async createCheckoutSession(planData, options = {}) {
    try {
      const { planId, tierId, billingCycle } = planData;
      const { reactivateSubscription: shouldReactivate, confirmPlanSwitch } = options;

      logger.log({
        tags: "manager|pricing|debug",
        color: "yellow"
      }, `Creating checkout session:`, { planId, tierId, billingCycle, shouldReactivate, confirmPlanSwitch });

      // Create checkout session - backend will automatically resolve price ID from tier.stripe config
      const checkoutResponse = await fetch('/api/stripe/create-checkout', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          planId,        // Plan uniqueId (e.g., 'pro', 'max')
          tierId,        // Tier id (e.g., 'basic', 'enhanced')
          billingCycle,  // 'monthly', 'annual', or 'one-time'
          reactivateSubscription: shouldReactivate || false,
          confirmPlanSwitch: confirmPlanSwitch || false,
          // priceId is optional - backend will auto-resolve from tier.stripe config
        }),
      });

      let checkoutData;
      try {
        checkoutData = await checkoutResponse.json();
      } catch (parseError) {
        logger.error({
          tags: "manager|pricing|error",
          color1: "red"
        }, "Failed to parse checkout response:", parseError);
        throw new Error('Invalid response from server');
      }

      // Handle non-200 responses
      if (!checkoutResponse.ok || !checkoutData.success) {
        const errorCode = checkoutData.error;
        const errorMessage = checkoutData.message || checkoutData.error || 'Failed to create checkout session';
        
        logger.error({
          tags: "manager|pricing|error",
          color1: "red"
        }, `Checkout creation failed (${checkoutResponse.status}): ${errorCode} - ${errorMessage}`);

        // Provide helpful error message for missing Stripe price ID
        if (errorMessage.includes('Stripe price ID not found')) {
          const helpfulMessage = `Stripe price ID not found for plan "${planId}", tier "${tierId}", billing "${billingCycle}".\n\n` +
            `Please link the Stripe product and price IDs to this plan tier using the "Link to Plan Tier" button in the Dev section (Stripe subsection).\n\n` +
            `Plan: ${planId}\n` +
            `Tier: ${tierId}\n` +
            `Billing Cycle: ${billingCycle}`;
          throw new Error(helpfulMessage);
        }
        
        throw new Error(errorMessage);
      }

      // Check if subscription was reactivated
      if (checkoutData.reactivated) {
        return {
          action: 'reactivated',
          message: checkoutData.message || 'Subscription reactivated successfully',
          subscriptionId: checkoutData.subscriptionId,
        };
      }

      if (!checkoutData.url) {
        throw new Error('Checkout session created but no URL returned');
      }

      logger.log({
        tags: "manager|pricing|action",
        color: "lime"
      }, `Checkout session created successfully, redirecting to Stripe: ${checkoutData.sessionId}`);

      // Redirect to Stripe checkout page
      // After successful payment, Stripe will redirect back to /checkout/success?session_id={CHECKOUT_SESSION_ID}
      window.location.href = checkoutData.url;
    } catch (error) {
      logger.error({
        tags: "manager|pricing|error",
        color1: "red"
      }, "Checkout session creation error:", error);
      throw error;
    }
  }

  /**
   * Handle incoming payload from other sections
   */
  handlePayload(payload) {
    if (!payload || !payload.action) return;

    logger.log({
      tags: "manager|pricing|payload",
      color: "yellow"
    }, `Received payload: ${payload.action}`);

    // Handle different payload actions
    switch (payload.action) {
      case 'upgrade_plan':
      case 'manage_subscription':
      case 'upgrade_credits':
        // Could set billing cycle or highlight specific plans based on action
        if (payload.currentPlan) {
          // Could filter or highlight plans based on current plan
        }
        break;
      default:
        logger.log({
          tags: "manager|pricing|payload",
          color: "yellow"
        }, `Unknown payload action: ${payload.action}`);
    }
  }

  /**
   * Get the current initialization status
   */
  getIsInitialized() {
    return this.isInitialized;
  }
}
